// AnocUploadBlock — upload + view a client's Annual Notice of Change (ANOC)
// documents. Stores the file in the private anoc-documents bucket via
// /api/medicare/anoc-upload and marks the medicare_anoc_reviews row 'received'.
// Closes the data-audit gap where ANOC files were never captured.

import React from 'react';
import { bearer } from '../lib/authedFetch';

const nextPlanYear = new Date().getUTCFullYear() + 1;

export default function AnocUploadBlock({ clientId }) {
  const [reviews, setReviews] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [toast, setToast] = React.useState(null);
  const [planYear, setPlanYear] = React.useState(nextPlanYear);
  const fileRef = React.useRef(null);

  const load = React.useCallback(async () => {
    if (!clientId) return;
    try {
      const r = await fetch(`/api/medicare/anoc-upload?client_id=${encodeURIComponent(clientId)}`, { headers: { ...(await bearer()) } });
      const j = await r.json().catch(() => ({}));
      if (j?.ok) setReviews(j.reviews || []);
    } catch { /* */ }
  }, [clientId]);
  React.useEffect(() => { load(); }, [load]);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true); setToast(null);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const rd = new FileReader();
        rd.onload = () => resolve(String(rd.result).replace(/^data:[^;]+;base64,/, ''));
        rd.onerror = reject; rd.readAsDataURL(file);
      });
      const r = await fetch('/api/medicare/anoc-upload', {
        method: 'POST', headers: { 'content-type': 'application/json', ...(await bearer()) },
        body: JSON.stringify({ client_id: clientId, plan_year: Number(planYear), filename: file.name, mime: file.type || 'application/pdf', base64 }),
      });
      const j = await r.json().catch(() => ({}));
      setToast(j?.ok ? `ANOC stored for ${j.plan_year}.` : (j?.error || 'Upload failed'));
      if (j?.ok) await load();
    } catch (e) { setToast(e.message); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; setTimeout(() => setToast(null), 4000); }
  };

  const download = async (path) => {
    try {
      const r = await fetch('/api/medicare/anoc-upload', { method: 'POST', headers: { 'content-type': 'application/json', ...(await bearer()) }, body: JSON.stringify({ action: 'download', document_path: path }) });
      const j = await r.json().catch(() => ({}));
      if (j?.url) window.open(j.url, '_blank', 'noopener'); else setToast(j?.error || 'Could not open document');
    } catch (e) { setToast(e.message); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Annual Notice of Change (ANOC)</div>
        <div className="flex items-center gap-2">
          <select value={planYear} onChange={(e) => setPlanYear(e.target.value)} className="text-[12px] border border-slate-300 rounded-md px-2 py-1">
            {[nextPlanYear, nextPlanYear - 1, nextPlanYear - 2].map((y) => <option key={y} value={y}>Plan year {y}</option>)}
          </select>
          <label className={`text-[12px] font-bold px-3 py-1.5 rounded-lg cursor-pointer ${busy ? 'bg-slate-200 text-slate-500' : 'bg-slate-900 text-white hover:bg-slate-700'}`}>
            {busy ? 'Uploading…' : 'Upload ANOC'}
            <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" disabled={busy} onChange={(e) => onFile(e.target.files?.[0])}/>
          </label>
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mt-1">The plan's yearly change notice. Stored privately; used to verify what actually changed before AEP.</div>

      {reviews.length > 0 ? (
        <div className="mt-3 space-y-1.5">
          {reviews.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 text-[13px] border-b border-slate-100 pb-1.5">
              <span><b>Plan year {r.plan_year}</b> <span className="text-[11px] text-slate-500">· {r.status}{r.received_at ? ` · ${new Date(r.received_at).toLocaleDateString()}` : ''}</span></span>
              {r.document_path && <button onClick={() => download(r.document_path)} className="text-[12px] font-bold text-sky-700 hover:text-sky-900">View</button>}
            </div>
          ))}
        </div>
      ) : <div className="mt-3 text-[12px] text-slate-500 italic">No ANOC on file yet. Upload the plan's Annual Notice of Change when it arrives.</div>}

      {toast && <div className="mt-2 text-[12px] text-slate-700">{toast}</div>}
    </div>
  );
}
